import { createAction } from '@reduxjs/toolkit'
import isNull from 'lodash/isNull'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { updateOpeningGroup } from '../../../api/takeoff-api'
import { OpeningGroupAPI } from '../../../models/activeDrawable'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Color, Select, Workspace } from '../../lib/toolBoxes/2D'
import { selectActiveHighlightId, selectKeyboardMultiSelectedItems, updateHighlightColor } from '../../slices/2D'
import { setHighlightPopupColor } from '../../slices/common'
import { addHighlightGroup, selectHighlights } from '../../slices/highlights'

export const updateHighlightSettingsAction = createAction<string>('updateHighlightSettings')

export function* getPaperIds() {
    const paperManager: PaperManager | null = yield call(managers.get2DManager)

    if (!paperManager) return

    const activeHighlightId = yield select(selectActiveHighlightId)
    const multiSelectedItemIds: number[] | null = yield select(selectKeyboardMultiSelectedItems)

    const itemsPaperIds: number[] | null = activeHighlightId
        ? [...(multiSelectedItemIds || []), activeHighlightId]
        : multiSelectedItemIds

    if (!itemsPaperIds) {
        const [selectTool]: [Select] = yield call(paperManager.getTools, [Select.NAME])

        const multiSelectedItems: paper.Item[] = yield call(selectTool.getMultiSelectedItems)

        if (multiSelectedItems.length === 0) return null

        return multiSelectedItems.map((item) => item.id)
    }

    return itemsPaperIds
}

export function* updateHighlightSettings({ payload }: ReturnType<typeof updateHighlightSettingsAction>) {
    try {
        const paperManager: PaperManager | null = yield call(managers.get2DManager)

        if (!paperManager) return

        const highlightOpeningGroups: OpeningGroupAPI[] | null = yield select(selectHighlights)

        if (isNull(highlightOpeningGroups)) return

        const [workspaceTool, colorTool]: [Workspace, Color] = yield call(paperManager.getTools, [
            Workspace.NAME,
            Color.NAME,
        ])

        const itemsPaperIds: number[] | null = yield call(getPaperIds)

        if (!itemsPaperIds) return

        const highlightPaperItems: paper.Path[] | null = yield call(workspaceTool.getItemsWithPaperIds, itemsPaperIds)

        if (isNull(highlightPaperItems)) return

        const highlightOpeningGroupIds = highlightPaperItems.map((highlight) => highlight.data.opening_group_id)

        const highlightGroups = highlightOpeningGroups.filter((highlight) =>
            highlightOpeningGroupIds.includes(highlight.id)
        )

        if (!highlightGroups) return

        const updatePromises = highlightGroups.map((group) => {
            const req = {
                openings: group.openings,
                settings: {
                    ...group.settings,
                    color: payload.replace('#', ''),
                },
                type: group.type,
            }

            return call(updateOpeningGroup, group.project_id, req, String(group.id))
        })

        const results = yield all(updatePromises)

        yield all(results.map((result) => put(addHighlightGroup(result))))

        yield put(setHighlightPopupColor(payload))

        highlightPaperItems.forEach((highlight) => {
            highlight.fillColor = colorTool.createColor(payload)
        })

        yield put(updateHighlightColor({ highlightGroupIds: highlightOpeningGroupIds, color: payload }))
    } catch (e) {
        console.error('Error changing highlight color: ', e)
    }
}

export function* watchForUpdateHighlightSettings() {
    yield takeLatest(updateHighlightSettingsAction.type, updateHighlightSettings)
}
